import { getCoverThumbUrl } from '../utils/coverUrl';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Quote, Star, Clock, Trash2 } from 'lucide-react';
import axios from 'axios';
import UserAvatar from './avatars/UserAvatar';
import BookwormCard from './avatars/BookwormCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const getCoverUrl = (url) => {
  if (!url) return 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=400&q=80';
  if (url.startsWith('http') || url.startsWith('data:')) return url;
  return url;
};

/**
 * RecentReviewsRow — used on both Landing page (logged-out / /welcome) and the
 * Home dashboard (logged-in). Admins see a trash overlay on each card.
 */
const RecentReviewsRow = ({ limit = 6, onDelete = null, dense = false }) => {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const userRole = typeof window !== 'undefined' ? localStorage.getItem('userRole') : null;
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const isAdmin = userRole === 'admin' && !!token;

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/reviews/recent?limit=${limit}`);
        if (mounted) setReviews(res.data || []);
      } catch {
        if (mounted) setReviews([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [limit]);

  const handleDelete = async (reviewId) => {
    if (!isAdmin) return;
    if (!window.confirm('Delete this review? This will remove it from the landing page AND the book details page.')) return;
    try {
      await axios.delete(`${API}/reviews/${reviewId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReviews((prev) => prev.filter((r) => r.id !== reviewId));
      if (onDelete) onDelete(reviewId);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to delete review');
    }
  };

  if (loading || reviews.length === 0) return null;

  return (
    <section
      className={`px-4 sm:px-6 lg:px-8 ${dense ? 'py-10' : 'py-16'} bg-gradient-to-b from-black via-zinc-900/70 to-black`}
      data-testid="home-recent-reviews-section"
    >
      <div className="max-w-screen-xl mx-auto">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="inline-flex items-center gap-2 bg-fuchsia-500/15 rounded-full px-3 py-1.5">
              <Quote className="h-4 w-4 text-fuchsia-300" />
              <span className="text-xs uppercase tracking-wider text-fuchsia-200 font-semibold">
                Community Reviews
              </span>
            </div>
            <h2 className={`${dense ? 'text-2xl' : 'text-3xl'} font-bold text-white`}>
              Just Reviewed
            </h2>
          </div>
          {isAdmin && (
            <span className="text-xs text-fuchsia-300/70">
              Admin: hover any card to delete
            </span>
          )}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {reviews.map((review, index) => (
            <div
              key={review.id || index}
              className="relative bg-white/5 backdrop-blur-sm rounded-xl p-5 border border-white/10 hover:border-fuchsia-400/40 transition-colors cursor-pointer"
              data-testid="recent-review-card"
              onClick={() => review.book_id && navigate(`/book/${review.book_id}`)}
            >
              {isAdmin && review.id && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleDelete(review.id); }}
                  className="absolute top-2 right-2 text-gray-400 hover:text-red-400 p-1.5 rounded-full bg-black/40 hover:bg-black/70 transition-colors z-10"
                  title="Delete review (admin)"
                  data-testid={`delete-recent-review-${review.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}

              <div className="flex items-start gap-3 mb-3">
                <img
                  src={getCoverThumbUrl(review.book_cover)}
                  alt={review.book_title}
                  className="w-12 h-18 object-cover rounded-md flex-shrink-0"
                  loading="lazy"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-white font-semibold truncate">{review.book_title}</p>
                  <p className="text-gray-400 text-xs truncate">by {review.book_author}</p>
                  <div className="flex items-center mt-1.5">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`h-3.5 w-3.5 ${i < (review.rating || 0) ? 'text-yellow-500 fill-yellow-500' : 'text-gray-600'}`}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <p className="text-gray-300 text-sm italic line-clamp-3 mb-3">"{review.comment}"</p>

              <div className="flex items-center justify-between pt-3 border-t border-white/10">
                <div
                  className="flex items-center gap-2 min-w-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  <BookwormCard
                    userId={review.user_id}
                    name={review.user_name}
                    avatarId={review.user_avatar_id}
                    avatarPhotoUrl={review.user_avatar_photo_url}
                  >
                    <span className="inline-flex items-center gap-2">
                      <UserAvatar
                        name={review.user_name}
                        avatarId={review.user_avatar_id}
                        avatarPhotoUrl={review.user_avatar_photo_url}
                        size="sm"
                        testId={`recent-review-avatar-${review.id || index}`}
                      />
                      <span className="text-gray-300 text-xs truncate">{review.user_name}</span>
                    </span>
                  </BookwormCard>
                </div>
                <div className="flex items-center text-gray-500 text-xs">
                  <Clock className="h-3 w-3 mr-1" />
                  {review.timestamp ? new Date(review.timestamp).toLocaleDateString() : ''}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default RecentReviewsRow;
